import { useEffect, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import LevelPicker from '../../components/LevelPicker';
import { labelForField, levelDims } from '../tracking/tracking-page.format';
import EntityFilterPicker, { type FilterEntry } from '../../components/EntityFilterPicker';
import TimezoneSelect from '../../components/TimezoneSelect';
import { DATE_PRESETS, type DatePresetKey } from '../../utils/date-presets';
import { offsetLabel } from '../../utils/timezone';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import { useAuthStore } from '../../stores/auth.store';
import {
  METRIC_OPTIONS, VIZ_OPTIONS, DIMENSION_FILTER_FIELDS, ENTITY_FILTER_FIELDS, FILTER_FIELD_LABELS, pivotDims, PIVOT_SERIES_CAP, canCombine, fitLevel,
} from './format';
import type { DashboardPanel, PanelViz } from './types';

export default function PanelEditorDialog({
  panel, open, onClose, onSave,
}: {
  panel: DashboardPanel | null;
  open: boolean;
  onClose: () => void;
  onSave: (panel: DashboardPanel) => void;
}) {
  const [draft, setDraft] = useState<DashboardPanel | null>(panel);
  const [boxField, setBoxField] = useState('offer_id');
  const [dimField, setDimField] = useState(DIMENSION_FILTER_FIELDS[0].value);
  const [dimValue, setDimValue] = useState('');
  const workspaces = useAuthStore((s) => s.user?.workspaces ?? []);
  const defaultTz = useReportDefaults().timezone;
  useEffect(() => { setDraft(panel); }, [panel]);
  if (!draft) return null;

  const q = draft.query;
  const setQuery = (patch: Partial<DashboardPanel['query']>) =>
    setDraft({ ...draft, query: { ...draft.query, ...patch } });

  const onFilters = (next: FilterEntry[] | ((prev: FilterEntry[]) => FilterEntry[])) =>
    setQuery({ filters: typeof next === 'function' ? next(draft.query.filters ?? []) : next });

  // Non-entity filters (country/device/v1-10/…) live in the same list but have no name
  // lookup, so they get a field-picker + free-text value here instead of EntityFilterPicker.
  const dimFilters = (draft.query.filters ?? []).filter((f) => !ENTITY_FILTER_FIELDS.includes(f.field));
  const addDim = () => {
    const value = dimValue.trim();
    if (!value) return;
    // One value per dimension field: the server binds text dimensions (country/device/v*/…)
    // as scalar equality, so a second value would be silently dropped. Replace, don't append.
    onFilters((prev) => [
      ...prev.filter((f) => f.field !== dimField),
      { field: dimField, value, label: value },
    ]);
    setDimValue('');
  };
  const removeDim = (field: string, value: string) =>
    onFilters((prev) => prev.filter((f) => !(f.field === field && f.value === value)));

  const dims = levelDims(q.group_by);
  const metricValues = METRIC_OPTIONS.filter((o) => q.metrics.includes(o.value));
  // Pie uses one metric; sort must be one of the selected metrics.
  const sortOptions = METRIC_OPTIONS.filter((o) => q.metrics.includes(o.value));
  const groupLabel = labelForField(dims[0]);
  // Let a panel sort by its group (e.g. Hour of Day in order), not only by a metric.
  const sortChoices = [...(dims.length ? [{ value: dims[0], label: `${groupLabel} (group)` }] : []), ...sortOptions.map((o) => ({ value: o.value, label: o.label }))];
  const isChart = draft.viz === 'line' || draft.viz === 'bar';
  const pivot = isChart ? pivotDims(q.group_by) : null;
  const isSummary = draft.viz === 'summary';
  const rightAxisValues = METRIC_OPTIONS.filter((o) => (q.y2_metrics ?? []).includes(o.value) && q.metrics.includes(o.value));

  // Summary panels render the fixed daily briefing — only the title matters.
  const canSave = draft.title.trim().length > 0 && (isSummary || (!!q.group_by && q.metrics.length > 0));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit panel</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Subtitle"
            value={draft.subtitle ?? ''}
            onChange={(e) => setDraft({ ...draft, subtitle: e.target.value || undefined })}
            size="small"
            fullWidth
            placeholder="optional"
          />
          <TextField
            select
            label="Visualization"
            value={draft.viz}
            onChange={(e) => {
              const viz = e.target.value as PanelViz;
              // Summary ignores the query but the schema still requires group_by + a metric,
              // so backfill placeholders rather than let an empty-metrics board 400 on save.
              const query = viz === 'summary'
                ? { ...draft.query, group_by: draft.query.group_by || 'date', metrics: draft.query.metrics.length ? draft.query.metrics : ['profit'] }
                : { ...draft.query, group_by: fitLevel(viz, draft.query.group_by) || 'date' };
              setDraft({ ...draft, viz, query });
            }}
            size="small"
            fullWidth
          >
            {VIZ_OPTIONS.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
          </TextField>
          {isSummary && (
            <>
              <Typography variant="body2" color="text.secondary">
                Shows the daily “what needs attention” briefing: one section per workspace in view, or only the one it is pinned to.
              </Typography>
              {workspaces.length > 1 && (
                <TextField
                  select
                  label="Workspace"
                  value={q.workspace_id ?? ''}
                  onChange={(e) => setQuery({ workspace_id: e.target.value || undefined })}
                  size="small"
                  sx={{ maxWidth: 260 }}
                  helperText="Pin this panel to one workspace, or follow the current selection"
                >
                  <MenuItem value="">Follow selection</MenuItem>
                  {workspaces.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </TextField>
              )}
            </>
          )}
          {!isSummary && (
          <>
          {draft.viz !== 'stat' && (
            <LevelPicker label="Group by" level={q.group_by} required canAdd={(dims, dim) => canCombine(draft.viz, dims, dim)}
              onChange={(group_by) => setQuery({ group_by, sort_by: undefined })} />
          )}
          {pivot && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              One {draft.viz === 'bar' ? 'bar' : 'line'} per {labelForField(pivot.split)} — the first metric, top {PIVOT_SERIES_CAP}
            </Typography>
          )}
          <Autocomplete
            multiple
            options={METRIC_OPTIONS}
            value={metricValues}
            onChange={(_e, v) => {
              const metrics = v.map((o) => o.value);
              setQuery({
                metrics,
                sort_by: metrics.includes(q.sort_by ?? '') ? q.sort_by : metrics[0],
                y2_metrics: (q.y2_metrics ?? []).filter((m) => metrics.includes(m)),
              });
            }}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => (
              <TextField {...params} label="Metrics" size="small" helperText={draft.viz === 'pie' ? 'Pie uses the first metric' : undefined} />
            )}
          />
          {isChart && q.metrics.length > 1 && (
            <Autocomplete
              multiple
              options={METRIC_OPTIONS.filter((o) => q.metrics.includes(o.value))}
              value={rightAxisValues}
              onChange={(_e, v) => setQuery({ y2_metrics: v.map((o) => o.value) })}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => (
                <TextField {...params} label="Right Y axis" size="small" helperText="Metrics to plot on a second axis (different scale)" />
              )}
            />
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Sort by"
              value={sortChoices.some((o) => o.value === q.sort_by) ? q.sort_by : (q.metrics[0] ?? dims[0])}
              onChange={(e) => setQuery({ sort_by: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            >
              {sortChoices.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Order"
              value={q.sort_order ?? 'desc'}
              onChange={(e) => setQuery({ sort_order: e.target.value as 'asc' | 'desc' })}
              size="small"
              sx={{ width: 120 }}
            >
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </TextField>
            <TextField
              label="Rows"
              type="number"
              value={q.limit ?? ''}
              onChange={(e) => setQuery({ limit: e.target.value ? Math.max(1, Math.min(1000, Number(e.target.value))) : undefined })}
              size="small"
              sx={{ width: 100 }}
              placeholder="auto"
            />
          </Stack>
          <FormControlLabel
            control={(
              <Switch
                checked={q.exclude_bots ?? true}
                onChange={(e) => setQuery({ exclude_bots: e.target.checked })}
              />
            )}
            label="Exclude bots"
          />
          {draft.viz === 'stat' && (
            <FormControlLabel
              control={(
                <Switch
                  checked={!!q.compare}
                  onChange={(e) => setQuery({ compare: e.target.checked || undefined })}
                />
              )}
              label="Compare to previous period (show delta)"
            />
          )}

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Filters</Typography></Divider>
          {workspaces.length > 1 && (
            <TextField
              select
              label="Workspace"
              value={q.workspace_id ?? ''}
              onChange={(e) => setQuery({ workspace_id: e.target.value || undefined })}
              size="small"
              sx={{ maxWidth: 260 }}
              helperText="Pin this panel to one workspace, or follow the current selection"
            >
              <MenuItem value="">Follow selection</MenuItem>
              {workspaces.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
            </TextField>
          )}
          <EntityFilterPicker
            filters={q.filters ?? []}
            onFilters={onFilters}
            field={boxField}
            onField={setBoxField}
          />

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                select size="small" label="Dimension" value={dimField}
                onChange={(e) => setDimField(e.target.value)} sx={{ minWidth: 150 }}
              >
                {DIMENSION_FILTER_FIELDS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </TextField>
              <TextField
                size="small" label="Value" value={dimValue} sx={{ flex: 1 }}
                placeholder="exact value, e.g. US or mobile"
                onChange={(e) => setDimValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDim(); } }}
              />
              <Button onClick={addDim} disabled={!dimValue.trim()} sx={{ mt: 0.5 }}>Add</Button>
            </Stack>
            {dimFilters.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {dimFilters.map((f) => (
                  <Chip
                    key={`${f.field}:${f.value}`}
                    size="small"
                    label={`${FILTER_FIELD_LABELS[f.field] ?? f.field}: ${f.value}`}
                    onDelete={() => removeDim(f.field, f.value)}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Time range</Typography></Divider>
          <FormControlLabel
            control={(
              <Switch
                checked={q.time_preset === undefined}
                onChange={(e) => setQuery({ time_preset: e.target.checked ? undefined : 'last_7_days' })}
              />
            )}
            label="Use dashboard range"
          />
          {q.time_preset !== undefined && (
            <TextField
              select
              label="Panel range"
              value={q.time_preset}
              onChange={(e) => setQuery({ time_preset: e.target.value as DatePresetKey })}
              size="small"
              sx={{ maxWidth: 220 }}
            >
              {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
            </TextField>
          )}
          <FormControlLabel
            control={(
              <Switch
                checked={q.timezone === undefined}
                onChange={(e) => setQuery({ timezone: e.target.checked ? undefined : defaultTz })}
              />
            )}
            label={`Use my default timezone (${offsetLabel(defaultTz)})`}
          />
          {q.timezone !== undefined && (
            <TimezoneSelect value={q.timezone} onChange={(v) => setQuery({ timezone: v })} sx={{ maxWidth: 220 }} />
          )}
          </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => onSave(draft)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
